if (!customElements.get('aplus-anchor-nav')) {
  customElements.define(
    'aplus-anchor-nav',
    class ApLusAnchorNav extends HTMLElement {
      // Cap on how long the "Shop the Room" tab waits for a candidate app widget
      // (Lookfy/SellEasy) to hydrate before giving up and removing the tab. Apps
      // fetch/render their own content client-side on their own schedule, so this
      // has to be a real wait, not a single check on connect -- but it can't wait
      // forever either, for a product where neither app is actually configured.
      // Matches Lookfy's own observed "widget not found within 10000ms" failure
      // window (docs/pdp/Shop-the-Room-Tab-Logic.md) -- a shorter cap here can
      // give up on a slow connection before Lookfy itself would have.
      static SHOP_THE_ROOM_TIMEOUT_MS = 10000;

      connectedCallback() {
        const allTabs = Array.from(this.querySelectorAll('.aplus-anchor-nav__tab'));
        // "Shop the Room" is marked with data-aplus-anchor-candidates instead of a
        // single data-aplus-anchor-target and starts with the `--pending` class
        // (see sections/aplus-anchor-nav.liquid) -- unlike the other tabs, its target is
        // rendered by a third-party app that hydrates client-side, so Liquid can't
        // know at render time whether it'll actually have content. Pull it out of
        // the normal tab set here; resolvePendingTab() decides its fate once the
        // rest of the nav is otherwise fully wired up.
        this.pendingTab = allTabs.find((tab) => tab.dataset.aplusAnchorCandidates) || null;
        this.tabs = allTabs.filter((tab) => tab !== this.pendingTab);
        // Desktop header auto-hides on scroll-down by pushing its own `top` negative
        // while staying `position: sticky` (see .shopify-section-header-hidden in
        // assets/base.css) rather than leaving the DOM/layout. --header-height is a
        // static value, so this bar must track the header's hidden state itself to
        // avoid a header-height-tall gap above it when the header slides away. This
        // affects both this bar's own fixed `top` (see getStickyOffset()/
        // syncHeaderOffset()) and where it decides to stick (observeStuck()) --
        // click-scroll landing position uses a separate, deliberately static CSS
        // scroll-margin-top (see assets/section-aplus-anchor-nav.css) that does
        // NOT depend on this state.
        this.header = document.querySelector('.header-announcement-wrapper');
        this.sentinel = this.parentElement.querySelector('.aplus-anchor-nav__sentinel');

        // Each tab's data-aplus-anchor-target is a bare JSON template section key
        // (e.g. "aplus-product-details"), not a full DOM id -- Shopify renders
        // JSON-template sections as `shopify-section-template--{numeric}__{key}`,
        // and the numeric part is environment-specific and unknowable from Liquid
        // in a sibling section. Resolve the real element by matching the id suffix.
        this.keyByElement = new Map();
        this.tabs.forEach((tab) => {
          const key = tab.dataset.aplusAnchorTarget;
          const target = this.resolveTarget(key);
          if (target) this.keyByElement.set(target, key);
        });
        this.sections = Array.from(this.keyByElement.keys());

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', (event) => this.handleTabClick(event, tab));
        });

        // Dimensions is the only tab whose content can change after load, since its section
        // falls back per variant -- every other tab's visibility is fixed at render time.
        this.dimensionsTab = this.tabs.find((tab) => tab.dataset.aplusAnchorTarget === 'aplus-dimensions') || null;
        if (this.dimensionsTab) {
          // eslint-disable-next-line no-undef
          this.dimensionsVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
            this.handleDimensionsVariantChange(event),
          );
        }

        this.observeSections();
        this.observeStuck();

        // Measure this bar's real height once (plus on resize, since it can wrap
        // to a different height at other breakpoints) and publish it as a CSS var
        // that the target sections' scroll-margin-top reads (see
        // assets/section-aplus-anchor-nav.css). Deliberately NOT re-measured per
        // click or tied to the header's hidden/revealed state -- see handleTabClick.
        this.updateNavHeight();
        window.addEventListener('resize', () => this.updateNavHeight());

        if (this.pendingTab) {
          this.resolvePendingTab();
        } else if (this.tabs.length === 0) {
          // Only reachable if Shop the Room was never even a candidate (no
          // pendingTab) and every other tab was hidden too -- shouldn't normally
          // happen since sections/aplus-anchor-nav.liquid gates the whole
          // component on tab_count > 0, but stay consistent with hideNav() below
          // rather than leaving an empty bar up.
          this.hideNav();
        }
      }

      disconnectedCallback() {
        this.dimensionsVariantUnsubscriber?.();
      }

      // Reads the same data-has-content flag sections/aplus-dimensions.liquid already
      // rendered per variant, instead of re-deriving the override/fallback resolution.
      handleDimensionsVariantChange({ data: { variant } }) {
        if (!variant || !this.dimensionsTab) return;

        const template =
          document.querySelector(`template[data-aplus-dimensions-variant="${variant.id}"]`) ||
          document.querySelector('template[data-aplus-dimensions-variant="__product_level__"]');
        const hasContent = template?.dataset.hasContent === 'true';

        // Inline style so it reliably wins over the tab's own display: inline-flex rule.
        this.dimensionsTab.style.display = hasContent ? '' : 'none';
      }

      resolveSection(key) {
        // Correct-by-construction path: sections/aplus-product-details.liquid,
        // aplus-why-we-love-it.liquid, aplus-dimensions.liquid and
        // aplus-materials-care.liquid each mark their own outer wrapper with
        // data-aplus-anchor-module="{key}" directly in their own markup --
        // independent of Shopify's own per-instance section key/id, so it works
        // whether the section was added by hand-editing the JSON template with
        // this exact key (matches the id-suffix selector below too) or via the
        // theme editor's "Add section" (which always mints a fresh random key,
        // breaking every id-based match below). querySelector only ever returns
        // the first match, same as every other multi-match case in this file --
        // if a module type is ever placed twice on one template, the first one
        // in DOM order wins for tab-scroll purposes.
        const marker = document.querySelector(`[data-aplus-anchor-module="${key}"]`);
        if (marker) {
          const wrapper = marker.closest('[id^="shopify-section-"]') || marker;
          // section-aplus-anchor-nav.css's scroll-margin-top rule is keyed on the
          // literal `[id$='__${key}']` selectors below, which this path doesn't
          // necessarily match (the wrapper's real auto-generated id can still
          // differ from `key`) -- add the same resolution-path-independent class
          // finalizePendingTab() already uses for Shop the Room, so a click still
          // lands under this bar's real height instead of the CSS's plain
          // default. Purely additive: doesn't remove or replace any existing
          // class, so nothing that already targets this element breaks.
          wrapper.classList.add('aplus-anchor-nav-scroll-target');
          return wrapper;
        }
        return document.querySelector(`[id$="__${key}"]`) || document.getElementById(`shopify-section-${key}`);
      }

      // The literal keys in data-aplus-anchor-candidates are Shopify's
      // auto-generated id for an "apps" JSON-template section -- assigned fresh,
      // per THEME, the moment someone adds that app block in that theme's editor.
      // Duplicating a theme keeps it, but two themes set up independently (e.g. a
      // dev theme not literally cloned from prod) can end up with different keys
      // for the exact same app block even though the section/asset code is
      // identical. resolveSection(key) alone can't survive that mismatch.
      //
      // This is the portable fallback: match on a piece of the app's OWN
      // rendered markup instead -- stable across any store/theme that app is
      // installed on, since the app's own code produces it, not Shopify's
      // per-theme section-key generator -- then walk up to the nearest
      // enclosing JSON-template section wrapper (every section gets one of
      // these `id="shopify-section-..."` divs no matter what its key is), since
      // that's the element scroll-margin-top/IntersectionObserver need.
      //
      // Each app's own rendered markup, keyed by the JSON-template section key
      // it's a fallback for. Lookfy's gallery widget renders with
      // `.lookfy-gallery.lookfy-section` (also relied on by
      // snippets/lookfy-events.liquid). SellEasy's widget renders as a
      // `<lb-upsell-widget>` custom element no matter which placement/variant
      // block it is (pal/cpal/cpac all use the same tag).
      static CONTENT_FALLBACK_SELECTORS = {
        '17782334886346a2e0': '.lookfy-gallery.lookfy-section',
        '17782331355433198d': 'lb-upsell-widget',
      };

      // A selector can match more than one element -- SellEasy in particular
      // is also used elsewhere on the page for an unrelated cross-sell widget
      // cluster near the top (e.g. buy-box pac/pal blocks), built from the same
      // widget tag/classes. resolveTarget() is only called once per candidate
      // (see resolvePendingTab), so whichever section this picks is watched for
      // the entire timeout window -- picking an empty instance here means the
      // real one is never even looked at, no matter how long we wait. So this
      // checks every match's enclosing section against hasRenderedContent and
      // prefers the first one that already has real content; only when none of
      // them do yet (still hydrating) does it fall back to the first match, so
      // resolvePendingTab's own retry loop can pick it up once it hydrates.
      resolveByContentSelector(key) {
        const selector = ApLusAnchorNav.CONTENT_FALLBACK_SELECTORS[key];
        if (!selector) return null;
        const matches = document.querySelectorAll(selector);
        if (!matches.length) return null;
        const sections = Array.from(matches)
          .map((match) => match.closest('[id^="shopify-section-"]'))
          .filter(Boolean);
        if (!sections.length) return null;
        return sections.find((section) => this.hasRenderedContent(section, key)) || sections[0];
      }

      // Single entry point for turning a key into an element -- tries the exact
      // key match first (cheap, and correct whenever this environment's JSON
      // template key matches what's hardcoded in sections/aplus-anchor-nav.liquid),
      // falling back to resolveByContentSelector() for the handful of keys that
      // have one registered. Used everywhere a tab's target needs resolving --
      // resolvePendingTab() and handleTabClick() both have to agree on the same
      // element, or a tab whose FIRST resolution only succeeded via the fallback
      // would silently fail to scroll on click (handleTabClick would otherwise
      // re-resolve by exact key alone on every click).
      resolveTarget(key) {
        return this.resolveSection(key) || this.resolveByContentSelector(key);
      }

      // Both apps inject their own config <script>/<style> tags into their
      // wrapper regardless of whether they have anything real to show -- a
      // plain el.textContent check counts that injected script/style text as
      // "content", so it can't tell an app block with real items from one
      // with none. Each candidate instead gets its own check for its actual
      // item container, keyed the same way as CONTENT_FALLBACK_SELECTORS:
      // Lookfy's real items live inside its `.kt-type-main` gallery mount
      // (present, but with zero children, when the gallery has no images --
      // a `lookfy-gallery-blank` class on the root is a second, earlier
      // empty state that has no `.kt-type-main` at all); SellEasy's cards
      // (`lb-upsell-flat-card`/`lb-upsell-long-card`) are simply absent from
      // the DOM until it has real recommendations.
      static CONTENT_CHECKS = {
        '17782334886346a2e0': (el) => {
          const widget = el.querySelector('.lookfy-gallery');
          if (!widget || widget.classList.contains('lookfy-gallery-blank')) return false;
          const mount = widget.querySelector('.kt-type-main');
          return !!mount && mount.children.length > 0;
        },
        '17782331355433198d': (el) => !!el.querySelector('lb-upsell-flat-card, lb-upsell-long-card'),
      };

      // An app block's server-rendered markup is frequently just an empty mount
      // point (a bare wrapper div) that the app's own script fills in later --
      // "the element exists" is a different question from "the app rendered
      // something into it". A registered CONTENT_CHECKS entry is the precise
      // signal; the generic check below is only a fallback for a candidate
      // with no such entry, and reads el.innerText rather than el.textContent
      // for the same reason CONTENT_CHECKS exists at all -- innerText reflects
      // only rendered, visible text, not the contents of <script>/<style> tags.
      hasRenderedContent(el, key) {
        if (!el) return false;
        const check = key && ApLusAnchorNav.CONTENT_CHECKS[key];
        if (check) return check(el);
        if (el.innerText && el.innerText.trim().length > 0) return true;
        return !!el.querySelector('img, picture, video, iframe, canvas, svg');
      }

      // Resolves the "Shop the Room" tab against its candidate keys (priority
      // order set by sections/aplus-anchor-nav.liquid: Lookfy gallery, then
      // SellEasy carousel). Tries an immediate check first in case a candidate
      // already has content, then falls back to watching every existing
      // candidate element for DOM mutations -- apps hydrate their widgets
      // asynchronously on their own schedule, so this can't be a single
      // point-in-time check -- capped by SHOP_THE_ROOM_TIMEOUT_MS so a product
      // with neither app configured (or an app that never renders anything for
      // it) doesn't leave the tab, and this observer, waiting indefinitely.
      resolvePendingTab() {
        const tab = this.pendingTab;
        const keys = (tab.dataset.aplusAnchorCandidates || '')
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);

        // Re-resolves every candidate on each attempt, rather than resolving
        // once up front -- a candidate's element (e.g. the app's own
        // <lb-upsell-widget> tag for resolveByContentSelector() candidates) can
        // be entirely absent from the DOM at the first check and still appear
        // moments later; the app injects/hydrates its own markup on its own
        // schedule, same as the content within an already-found element. An
        // element that's genuinely never coming (a truly disabled section) and
        // one that just hasn't rendered yet are indistinguishable from a single
        // point-in-time check -- only a bounded retry window can tell them apart.
        const tryResolve = () => {
          const candidates = keys.map((key) => ({ key, el: this.resolveTarget(key) })).filter((candidate) => candidate.el);
          const match = candidates.find((candidate) => this.hasRenderedContent(candidate.el, candidate.key));
          if (match) {
            this.finalizePendingTab(match.key, match.el);
            return true;
          }
          return false;
        };

        if (tryResolve()) return;

        // Observes document.body rather than the individual candidate elements
        // this used to resolve up front -- a candidate that doesn't exist yet
        // has nothing to attach an observer to, so watching a stable ancestor
        // that's always present is what lets a not-yet-rendered candidate be
        // picked up at all. attributes/attributeFilter is still needed
        // alongside childList/subtree: Lookfy signals its own state through the
        // `lookfy-gallery-blank` class on its gallery root rather than only
        // through added/removed nodes, so a class-only mutation needs to
        // trigger a recheck too.
        const observer = new MutationObserver(() => tryResolve());
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        this.pendingObserver = observer;

        this.pendingTimeout = setTimeout(() => {
          if (!tryResolve()) this.finalizePendingTab(null, null);
        }, ApLusAnchorNav.SHOP_THE_ROOM_TIMEOUT_MS);
      }

      // targetKey/targetEl null means no candidate ever produced content -- the
      // tab is removed outright rather than left in the DOM hidden, since nothing
      // will revisit this decision later. Otherwise wires the tab up exactly like
      // one of the normal, Liquid-resolved tabs: same click handler, and folded
      // into the same keyByElement/sections/IntersectionObserver the other tabs
      // use for scroll-spy, so it behaves identically to them from here on.
      finalizePendingTab(targetKey, targetEl) {
        if (this.pendingObserver) this.pendingObserver.disconnect();
        if (this.pendingTimeout) clearTimeout(this.pendingTimeout);
        this.pendingObserver = null;
        this.pendingTimeout = null;

        const tab = this.pendingTab;
        this.pendingTab = null;

        if (!targetKey || !targetEl) {
          tab.remove();
          if (this.tabs.length === 0) this.hideNav();
          return;
        }

        tab.href = `#${targetKey}`;
        tab.dataset.aplusAnchorTarget = targetKey;
        delete tab.dataset.aplusAnchorCandidates;
        tab.classList.remove('aplus-anchor-nav__tab--pending');
        tab.removeAttribute('tabindex');
        tab.removeAttribute('aria-hidden');
        tab.addEventListener('click', (event) => this.handleTabClick(event, tab));

        // assets/section-aplus-anchor-nav.css's scroll-margin-top rule is keyed
        // on the literal ids too -- harmless when resolveTarget() found targetEl
        // via the exact key (its real id already matches one of those selectors),
        // but if it only matched via resolveByContentSelector() (literal key
        // mismatched on this environment), targetEl's real id won't match any of
        // them and scrolling would fall back to the CSS's generic 8rem default
        // instead of this bar's actual measured height. This class gives the CSS
        // a resolution-path-independent hook so the offset is exact either way.
        targetEl.classList.add('aplus-anchor-nav-scroll-target');

        this.tabs.push(tab);
        this.keyByElement.set(targetEl, targetKey);
        this.sections.push(targetEl);
        if (this.sectionsObserver) this.sectionsObserver.observe(targetEl);
      }

      // Only reachable when the nav ends up with zero visible tabs (Shop the Room
      // was the only candidate and neither app rendered anything) -- an anchor
      // nav with no tabs is just an empty bordered bar taking up space, so the
      // whole component (sentinel + nav + spacer live together in the same
      // analytics-module wrapper) comes out rather than leaving that behind.
      hideNav() {
        if (this.stuckObserver) this.stuckObserver.disconnect();
        if (this.sectionsObserver) this.sectionsObserver.disconnect();
        const wrapper = this.closest('analytics-module') || this;
        wrapper.style.display = 'none';
      }

      updateNavHeight() {
        document.documentElement.style.setProperty('--aplus-anchor-nav-height', `${this.offsetHeight}px`);
      }

      // Landing position is entirely delegated to the browser via native
      // scrollIntoView + the target's own CSS scroll-margin-top -- no per-click
      // offset math here. That's deliberate: a JS-computed offset (this file
      // previously read --header-height/offsetHeight fresh on every click) gave
      // the same click a different result depending on the header's in-the-moment
      // hidden/revealed state, so repeat clicks on the same tab landed
      // inconsistently. A static CSS formula is identical every time.
      //
      // The one case that formula can't cover: scrolling UP to a target is what
      // triggers the header's own on-scroll-up reveal (sections/header.liquid
      // StickyHeader.reveal()), so the header ends up occupying space it wasn't
      // reserved for at click time. Downward clicks never hit this -- the header
      // only reveals on upward scroll -- so only that direction needs the header's
      // height added on top of the static formula.
      scrollToTarget(target) {
        const currentTop = target.getBoundingClientRect().top;
        if (currentTop < 0) {
          // Read the header's live height rather than the --header-height custom
          // property: that var is only refreshed on init/resize/breakpoint change
          // (see sections/header.liquid setHeaderHeight()), so it can under-report
          // if the header's content (e.g. the proxy-subnav category row appended
          // after DOMContentLoaded) settles into a taller size afterward -- leaving
          // a gap between the revealed header and this bar.
          const headerHeight = this.header ? this.header.offsetHeight : 0;
          // Same "- 3rem" breathing room the static formula in
          // section-aplus-anchor-nav.css uses, converted to px off the root
          // font-size so it matches at any --font-body-scale setting.
          const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          const targetTop = currentTop + window.scrollY;
          window.scrollTo({ top: targetTop - headerHeight - this.offsetHeight + rootFontSize * 3, behavior: 'smooth' });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      // A single scrollToTarget() call computes the destination once, from
      // whatever the page's layout looks like at that exact moment. Sections
      // between the current scroll position and the target -- and the target's
      // own section -- can still be loading: images, and third-party widgets
      // that mount asynchronously (a product gallery app, a reviews widget, a
      // social feed embed) all keep growing their containers for a while after
      // the click, which pushes everything below them further down the page.
      // A destination computed before that settles is stale by the time the
      // animation finishes, landing short of where the target actually ended
      // up -- a click before things finish loading undershoots; the same click
      // once everything's already loaded lands correctly. This re-issues
      // scrollToTarget() every time the page's overall height changes, for a
      // bounded window (matching the ~10s a mounting widget can take before it
      // gives up), so the final rest position tracks the target's real position
      // instead of a one-time snapshot.
      //
      // Stops correcting if the user takes over scrolling manually, detected
      // via a real wheel/touch input rather than by comparing scrollY deltas --
      // this component's OWN smooth-scroll animation produces scrollY jumps
      // between ResizeObserver ticks just as large as a manual scroll would,
      // so a delta-based guard can't tell the two apart and would end up
      // disconnecting itself on its own animation almost immediately.
      keepScrollSynced(target) {
        const deadline = Date.now() + 10000;
        let userTookOver = false;
        const markUserTookOver = () => {
          userTookOver = true;
        };
        window.addEventListener('wheel', markUserTookOver, { passive: true, once: true });
        window.addEventListener('touchstart', markUserTookOver, { passive: true, once: true });

        const stop = () => {
          observer.disconnect();
          window.removeEventListener('wheel', markUserTookOver);
          window.removeEventListener('touchstart', markUserTookOver);
        };

        const observer = new ResizeObserver(() => {
          if (userTookOver || Date.now() > deadline) {
            stop();
            return;
          }
          this.scrollToTarget(target);
        });

        observer.observe(document.body);
        setTimeout(stop, 10000);
      }

      handleTabClick(event, tab) {
        const key = tab.dataset.aplusAnchorTarget;
        const target = this.resolveTarget(key);
        if (!target) return;

        event.preventDefault();

        // section-aplus-anchor-nav.css's scroll-margin-top formula (mobile
        // variant) reads --header-height and --aplus-anchor-nav-height, but
        // both are only refreshed on load/resize/breakpoint-change (see
        // sections/header.liquid setHeaderHeight(), updateNavHeight() below) --
        // not on every click. If the header's real height settled differently
        // since the last refresh (e.g. an announcement bar/carousel resizing
        // after initial paint), scrollIntoView() lands using a stale value and
        // the target ends up partly behind the fixed header/nav. Re-measuring
        // both right before every scroll, the same live-offsetHeight approach
        // scrollToTarget() already uses for its own upward-scroll case, keeps
        // the CSS formula's inputs accurate regardless of what changed since
        // the last resize event.
        if (this.header) {
          document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
        }
        this.updateNavHeight();

        // Analytics: Publish anchor nav tab click event (Path A - direct publish)
        const linkTitle = tab.dataset.analyticsLinkTitle || tab.textContent.trim();
        const linkText = tab.dataset.linkText || 'anchorNavClick';
        const clickData = {
          pageName: window.location.pathname,
          pageType: document.documentElement.dataset.pageType || 'product',
          moduleName: 'pdp:aplus:anchorNav',
          linkTitle: linkTitle,
          linkText: linkText,
          targetURL: null, // No navigation, smooth scroll only
          locURL: window.location.pathname,
          filters: null,
          searchTerm: null,
        };
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish('page-click', { clickData });
        }

        this.scrollToTarget(target);
        this.keepScrollSynced(target);

        history.pushState(null, '', `#${key}`);
      }

      // Mirrors header.liquid's own behavior: hide() is a no-op below 768px, so
      // the header never gains .shopify-section-header-hidden there and this
      // always returns the full header height on mobile. On desktop it returns 0
      // while the header is hidden, matching wherever the header bar actually is
      // right now instead of always reserving space for it.
      //
      // Reads this.header.offsetHeight live rather than the --header-height CSS
      // custom property: that var is only refreshed by header.liquid's own
      // setHeaderHeight() on init/resize/breakpoint change, so it can be stale by
      // the time the header reveals itself again on scroll-up (e.g. a promo bar
      // or subnav row changing the header's real height in between). Positioning
      // this bar's fixed `top` off that stale value is exactly what opened a
      // visible gap between the revealed header and this bar while scrolling up --
      // handleTabClick() already reads the header live for the same reason, this
      // just applies the same fix here, where the stuck bar's actual position is
      // decided.
      getStickyOffset() {
        if (this.header && this.header.classList.contains('shopify-section-header-hidden')) {
          return 0;
        }
        return this.header ? this.header.offsetHeight : 0;
      }

      // Keeps the fixed bar's own `top` in sync with the exact same offset
      // observeStuck() uses to decide WHEN to stick -- both must read from
      // getStickyOffset() so the bar can't stick at one line but render pinned to
      // a different one.
      syncHeaderOffset() {
        this.style.setProperty('--aplus-anchor-nav-top', `${this.getStickyOffset()}px`);
      }

      // CSS `position: sticky` alone can't hold this bar up through the rest of
      // the A+ content: its containing block is its own section wrapper, which is
      // only as tall as the nav itself (this section has no other content) --
      // "aplus-product-details", "aplus-why-we-love-it" etc. are later SIBLING
      // sections, not descendants of it, so a sticky bar runs out of containing
      // block and scrolls away again almost immediately after sticking. Switching
      // to `position: fixed` (see .is-stuck in section-aplus-anchor-nav.css) has
      // no such limit, since fixed is relative to the viewport, not a container.
      //
      // IntersectionObserver (not a manual scroll-position/getBoundingClientRect
      // comparison) drives the toggle: it's computed by the browser itself off
      // the sentinel's real, current layout on every relevant layout/scroll event,
      // so it can't go stale the way a one-off rAF measurement taken before
      // images/fonts finish loading can.
      observeStuck() {
        if (!this.sentinel) return;

        const rebuild = () => {
          if (this.stuckObserver) this.stuckObserver.disconnect();
          this.syncHeaderOffset();
          const offset = this.getStickyOffset();
          this.stuckObserver = new IntersectionObserver(
            ([entry]) => {
              // entry.isIntersecting alone is NOT enough: it's false both when the
              // sentinel has scrolled up past the stick line (what should trigger
              // is-stuck) AND, before the page has scrolled at all, simply because
              // the sentinel starts below the fold and hasn't entered the viewport
              // from the bottom yet (main-product is taller than one screen on
              // most products). boundingClientRect.top disambiguates which side of
              // the line it's actually on -- only "above" means stuck.
              this.classList.toggle('is-stuck', entry.boundingClientRect.top < offset);
            },
            { root: null, rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 },
          );
          this.stuckObserver.observe(this.sentinel);
        };

        rebuild();

        // getStickyOffset() changes when the header hides/reveals or the
        // viewport crosses the mobile/desktop breakpoint -- IntersectionObserver
        // can't have its rootMargin updated in place, so rebuild it when either
        // happens instead.
        if (this.header) {
          new MutationObserver(rebuild).observe(this.header, { attributes: true, attributeFilter: ['class'] });
        }
        window.addEventListener('resize', rebuild);
      }

      observeSections() {
        // Still sets up the observer even with zero sections right now if a
        // pending "Shop the Room" tab might add one later (finalizePendingTab()
        // calls this.sectionsObserver.observe() once it resolves) -- only truly
        // nothing to ever watch bails out early.
        if (!this.sections.length && !this.pendingTab) return;

        // Thin activation band just below the sticky bars, near the top of the
        // viewport, instead of a visibility-ratio threshold -- a threshold misfires
        // across sections of very different heights (a short Dimensions table vs. a
        // long Product Details module never both reach the same ratio cleanly).
        // Mirrors the exact same header-height rule the CSS scroll-margin-top uses
        // (section-aplus-anchor-nav.css, the 767px breakpoint): on desktop the
        // header hides itself on scroll-down (see getStickyOffset() above), so a
        // downward tab click never actually has it occupying space at rest and this
        // line must NOT reserve room for it. On mobile, header.liquid's hide() is a
        // no-op below 768px -- the header stays fixed/visible the whole time -- so
        // scrollIntoView() lands sections that much further down the page than this
        // line previously assumed. That gap was the actual bug: right after landing
        // on "Why We Love It" on mobile, its top was still just below the (too-short)
        // line, so it never qualified as a candidate and the previous section
        // ("Product Details") -- still technically the only qualifying one -- stayed
        // marked active instead of switching.
        const getActivationLine = () => {
          const isMobile = window.innerWidth < 768;
          const headerHeight = isMobile && this.header ? this.header.offsetHeight : 0;
          return headerHeight + this.offsetHeight + 80;
        };

        // Deliberately does NOT trust entry.isIntersecting / entries-array order to
        // decide which tab goes active. The activation band above is wide enough
        // that two adjacent sections (e.g. the tail of Product Details and the head
        // of Why We Love It) can both be intersecting it in the very same callback
        // batch -- and the spec doesn't guarantee which of their entries the browser
        // delivers last, so "whichever entry we process last wins" was a coin flip.
        // That's what produced the wrong tab lighting up on scroll-up, and a tab
        // click sometimes leaving the PREVIOUS tab active instead of the clicked
        // one's destination. Instead, every tick recomputes fresh from
        // getBoundingClientRect() across ALL sections and picks exactly one
        // deterministic winner.
        //
        // The two scroll directions deliberately use two different rules, because
        // they read as different things to someone reading the page:
        //
        // - Scrolling DOWN: a tab should keep pointing at what's still on screen.
        //   The active tab only advances to the next section once the current
        //   section has completely left the visible area below the sticky bars
        //   (its bottom has crossed above the line) -- not merely once the next
        //   section's top has started peeking in underneath it, which used to jump
        //   the highlight to the next tab while the current section was still ~60%
        //   visible.
        // - Scrolling UP: the active tab should switch back once the earlier
        //   section is actually recognizable again -- at least 50% visible -- same
        //   as before. A bare top-crossing here would light up a section's tab the
        //   instant its very first pixel peeked out from under the sticky bars,
        //   before there was enough on screen to recognize it.
        //
        // For a section shorter than the visible viewport, "50% visible" means half
        // of the section's OWN height is on screen; for a section taller than the
        // visible viewport (most of these run longer than one screen), it instead
        // means the section already fills at least half of the visible viewport --
        // otherwise a long section could scroll through entirely without ever
        // reaching 50% of its own (much larger) height.
        let lastScrollY = window.scrollY;
        let hasRunOnce = false;

        const currentActiveSection = () => {
          if (!this.activeKey) return null;
          for (const [section, key] of this.keyByElement.entries()) {
            if (key === this.activeKey) return section;
          }
          return null;
        };

        // Lowest section on the page whose top has already crossed the line --
        // used both for the very first run (nothing active yet to protect) and to
        // pick the NEXT section once scrolling down has fully cleared the current one.
        const findByTopCrossed = (line) => {
          let winner = null;
          this.sections.forEach((section) => {
            const top = section.getBoundingClientRect().top;
            if (top <= line && (!winner || top > winner.top)) {
              winner = { section, top };
            }
          });
          return winner ? winner.section : null;
        };

        const findByHalfVisible = (line, viewportBottom) => {
          const visibleViewportHeight = Math.max(0, viewportBottom - line);
          let winner = null;
          let winnerRatio = 0;
          this.sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const visibleTop = Math.max(rect.top, line);
            const visibleBottom = Math.min(rect.bottom, viewportBottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            const denominator = Math.min(rect.height, visibleViewportHeight);
            const ratio = denominator > 0 ? visibleHeight / denominator : 0;
            if (ratio >= 0.5 && ratio > winnerRatio) {
              winner = section;
              winnerRatio = ratio;
            }
          });
          return winner;
        };

        const activate = (section) => {
          if (!section) return;
          const key = this.keyByElement.get(section);
          if (key) this.setActiveTab(key);
        };

        const updateActiveFromPositions = () => {
          const line = getActivationLine();
          const viewportBottom = window.innerHeight;
          const currentScrollY = window.scrollY;
          const scrollingDown = currentScrollY > lastScrollY;
          lastScrollY = currentScrollY;

          if (!hasRunOnce) {
            hasRunOnce = true;
            activate(findByTopCrossed(line));
            return;
          }

          if (scrollingDown) {
            const active = currentActiveSection();
            if (active) {
              const rect = active.getBoundingClientRect();
              // Still something of the current section visible below the sticky
              // bars -- don't advance yet, even if the next section has started
              // to appear underneath it.
              if (rect.bottom > line) return;
            }
            activate(findByTopCrossed(line));
          } else {
            // Scrolling up (or no vertical movement, e.g. a resize) -- 50%-visible rule.
            activate(findByHalfVisible(line, viewportBottom));
          }
        };

        // IntersectionObserver still drives WHEN we bother recomputing (cheaper than
        // a raw scroll listener alone), it just no longer drives WHAT we decide.
        // Kept as this.sectionsObserver (not a local const) so finalizePendingTab()
        // can register the "Shop the Room" section with the same observer once it
        // resolves, instead of needing a second one.
        this.sectionsObserver = new IntersectionObserver(updateActiveFromPositions, {
          root: null,
          rootMargin: `-${getActivationLine()}px 0px -60% 0px`,
          threshold: 0,
        });

        this.sections.forEach((section) => this.sectionsObserver.observe(section));

        // Also recompute on every scroll/resize tick: a tab click's smooth scroll
        // can cross the activation line for the destination section and the
        // previous section within the same animation frame, and we want the final
        // settled position to win rather than whichever mid-scroll frame happened
        // to fire an IntersectionObserver callback last.
        window.addEventListener('scroll', updateActiveFromPositions, { passive: true });
        window.addEventListener('resize', updateActiveFromPositions);

        // Run once immediately too, in case the page loads already scrolled past
        // one or more sections (e.g. a deep link with a #aplus-... hash).
        updateActiveFromPositions();
      }

      setActiveTab(key) {
        this.activeKey = key;
        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.aplusAnchorTarget === key;
          tab.classList.toggle('is-active', isActive);
          if (isActive) {
            tab.setAttribute('aria-current', 'true');
          } else {
            tab.removeAttribute('aria-current');
          }
        });
      }
    },
  );
}
